import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, TrendingUp, Sparkles, X, ChevronRight,
  Camera, FileText, Award, IndianRupee, Briefcase, BadgeCheck, ScrollText, Video,
} from "lucide-react";

// ---------------------------------------------------------------------------
// ProfileStrengthCard — prominent "your profile is X% complete" meter on the
// Doctor Dashboard. Renders missing items as tappable rows that deep-link to
// the exact field needing attention (photo card / fee input / bio modal etc).
// ---------------------------------------------------------------------------
const ICON = {
  photo:          Camera,
  bio:            FileText,
  intro_video:    Video,
  qualifications: Award,
  experience:     Briefcase,
  fee:            IndianRupee,
  registration:   BadgeCheck,
  license:        ScrollText,
};

// Where to jump when the user taps a missing item. Most live on the same page,
// scrolled into view via a hash anchor; bio needs a modal handled locally.
const SCROLL_TO = {
  photo:          "#doctor-photo-card",
  intro_video:    "#doctor-intro-video-card",
  qualifications: "#doctor-quals-card",
  experience:     "#doctor-quals-card",
  registration:   "#doctor-quals-card",
  fee:            "#doctor-fee-card",
  license:        "#doctor-license-card",
};

export default function ProfileStrengthCard({ onOpenBio }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/doctors/me/profile-strength");
      setData(r.data);
    } catch {
      // non-fatal — card just hides
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Expose a refresh hook so parent pages can call after mutations
  useEffect(() => {
    const h = () => load();
    window.addEventListener("noi:profile-refresh", h);
    return () => window.removeEventListener("noi:profile-refresh", h);
  }, []);

  if (loading || !data) return null;
  const { score, items, missing_count } = data;
  const isDone = score >= 100;

  const jump = (key) => {
    if (key === "bio") { onOpenBio?.(); return; }
    const sel = SCROLL_TO[key];
    if (sel) {
      const el = document.querySelector(sel);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const barColor = score >= 100 ? "bg-emerald-500"
    : score >= 70 ? "bg-blue-600"
    : score >= 40 ? "bg-amber-500"
    : "bg-rose-500";

  return (
    <Card
      className="mt-6 p-5 sm:p-6 border-slate-200 bg-gradient-to-br from-white to-blue-50/50"
      data-testid="profile-strength-card"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
          {isDone ? <Sparkles className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              {isDone ? "Your profile is complete!" : "Complete your profile"}
            </h3>
            <span className={`font-heading font-bold text-xl ${isDone ? "text-emerald-600" : "text-blue-700"}`}>
              {score}%
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
            {isDone
              ? "You're featured first in patient search. Amazing work."
              : `${missing_count} ${missing_count === 1 ? "item" : "items"} to go. Doctors with complete profiles get ~3× more bookings.`}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="w-full h-2.5 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100}>
          <div
            className={`h-full ${barColor} transition-[width] duration-700 ease-out rounded-full`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Missing items */}
      {!isDone && (
        <div className="mt-5 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Next up</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.filter(i => !i.done).map((i) => {
              const Icon = ICON[i.key] || Circle;
              return (
                <button
                  key={i.key}
                  type="button"
                  onClick={() => jump(i.key)}
                  className="group flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 transition text-left"
                  data-testid={`profile-missing-${i.key}`}
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-slate-100 text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-700 grid place-items-center transition">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 line-clamp-1">{i.label}</div>
                    <div className="text-[11px] text-slate-500">+{i.weight}% when done</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Done items (collapsed summary) */}
      {items.some(i => i.done) && !isDone && (
        <details className="mt-4 text-xs text-slate-500 cursor-pointer">
          <summary className="hover:text-slate-700 select-none">
            {items.filter(i => i.done).length} already done — show
          </summary>
          <ul className="mt-2 space-y-1 pl-1">
            {items.filter(i => i.done).map(i => (
              <li key={i.key} className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {i.label}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BioEditorModal — lightweight inline modal so doctors can fill in their bio
// without leaving the dashboard. Kept in the same file because it's the only
// missing-item action that needs its own UI.
// ---------------------------------------------------------------------------
export function BioEditorModal({ open, onClose, initialBio = "", onSaved }) {
  const [bio, setBio] = useState(initialBio || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setBio(initialBio || ""); }, [open, initialBio]);
  if (!open) return null;
  const save = async () => {
    if (bio.trim().length < 40) {
      toast.error("Please write at least 40 characters about yourself.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/doctors/me/bio", { bio: bio.trim() });
      toast.success("Bio saved");
      onSaved?.(bio.trim());
      window.dispatchEvent(new Event("noi:profile-refresh"));
      onClose();
    } catch {
      toast.error("Could not save bio. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-heading font-semibold text-slate-900">About you</div>
            <div className="text-xs text-slate-500 mt-0.5">Patients read this on your profile card before booking.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Textarea
            rows={6}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="e.g. Consultant Neurosurgeon specialising in brain tumours, stroke care and spine surgery. 15 years at NIMHANS Bengaluru. I give clear, unrushed second opinions in English, Telugu and Hindi."
            data-testid="bio-editor-textarea"
            maxLength={1500}
          />
          <div className="flex items-center justify-between text-xs">
            <span className={bio.trim().length < 40 ? "text-rose-600" : "text-slate-500"}>
              {bio.trim().length} / 1500 characters
              {bio.trim().length < 40 && " — at least 40 needed"}
            </span>
            <span className="text-slate-400">English, Telugu, Hindi — whatever feels natural.</span>
          </div>
        </div>
        <div className="p-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || bio.trim().length < 40}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="bio-save-btn"
          >
            {busy ? "Saving…" : "Save bio"}
          </Button>
        </div>
      </div>
    </div>
  );
}
